import type {
  IntegrationFilterState,
  IntegrationStageDateRange,
  IntegrationStageFilterFields,
} from "./integration-filter-modal/integrationFilterTypes";
import {
  isValidationStage,
  updateStageFilter,
} from "./integration-filter-modal/integrationFilterTypes";

export type IntegrationFilterTag = {
  key: string;
  label: string;
  remove: (filter: IntegrationFilterState) => IntegrationFilterState;
};

const appendArrayFieldTags = (
  tags: IntegrationFilterTag[],
  filter: IntegrationFilterState,
  field: keyof IntegrationFilterState,
  labelForValue?: (value: string) => string
) => {
  const values = filter[field];
  if (!Array.isArray(values)) return;

  values.forEach((value) => {
    tags.push({
      key: `${field}-${value}`,
      label: labelForValue ? labelForValue(value) : value,
      remove: (current) => ({
        ...current,
        [field]: (current[field] as string[]).filter((item) => item !== value),
      }),
    });
  });
};

const formatDateRangeLabel = (
  stage: string,
  rangeType: "Start" | "End",
  range: IntegrationStageDateRange,
  fromKey: keyof IntegrationStageDateRange,
  toKey: keyof IntegrationStageDateRange
): string => {
  const from = range[fromKey] ?? "…";
  const to = range[toKey] ?? "…";
  return `${stage} ${rangeType}: ${from} - ${to}`;
};

const appendStageDateTags = (
  tags: IntegrationFilterTag[],
  stage: string,
  range: IntegrationStageDateRange
) => {
  if (range.startDateFrom || range.startDateTo) {
    tags.push({
      key: `${stage}-start-date`,
      label: formatDateRangeLabel(
        stage,
        "Start",
        range,
        "startDateFrom",
        "startDateTo"
      ),
      remove: (current) => {
        const fields = current.stageFilters[stage];
        if (!fields) return current;

        return updateStageFilter(current, stage, {
          ...fields,
          dateRange: {
            ...fields.dateRange,
            startDateFrom: null,
            startDateTo: null,
          },
        });
      },
    });
  }

  if (range.endDateFrom || range.endDateTo) {
    tags.push({
      key: `${stage}-end-date`,
      label: formatDateRangeLabel(stage, "End", range, "endDateFrom", "endDateTo"),
      remove: (current) => {
        const fields = current.stageFilters[stage];
        if (!fields) return current;

        return updateStageFilter(current, stage, {
          ...fields,
          dateRange: {
            ...fields.dateRange,
            endDateFrom: null,
            endDateTo: null,
          },
        });
      },
    });
  }
};

const appendStageFieldTags = (
  tags: IntegrationFilterTag[],
  stage: string,
  fields: IntegrationStageFilterFields
) => {
  if (isValidationStage(stage)) {
    fields.validationStatuses.forEach((value) => {
      tags.push({
        key: `${stage}-validationStatus-${value}`,
        label: `${stage} Validation: ${value}`,
        remove: (current) => {
          const fields = current.stageFilters[stage];
          if (!fields) return current;

          return updateStageFilter(current, stage, {
            ...fields,
            validationStatuses: fields.validationStatuses.filter(
              (item) => item !== value
            ),
          });
        },
      });
    });

    fields.endorsementLevel.forEach((value) => {
      tags.push({
        key: `${stage}-endorsement-${value}`,
        label: `${stage} Endorsement: ${value}`,
        remove: (current) => {
          const fields = current.stageFilters[stage];
          if (!fields) return current;

          return updateStageFilter(current, stage, {
            ...fields,
            endorsementLevel: fields.endorsementLevel.filter(
              (item) => item !== value
            ),
          });
        },
      });
    });

    if (fields.validationScoreValue.trim()) {
      tags.push({
        key: `${stage}-validationScore`,
        label: `${stage} Score: ${fields.validationScoreOperator} ${fields.validationScoreValue}`,
        remove: (current) => {
          const fields = current.stageFilters[stage];
          if (!fields) return current;

          return updateStageFilter(current, stage, {
            ...fields,
            validationScoreValue: "",
          });
        },
      });
    }
  } else {
    fields.integrationStatuses.forEach((value) => {
      tags.push({
        key: `${stage}-integrationStatus-${value}`,
        label: `${stage}: ${value}`,
        remove: (current) => {
          const fields = current.stageFilters[stage];
          if (!fields) return current;

          return updateStageFilter(current, stage, {
            ...fields,
            integrationStatuses: fields.integrationStatuses.filter(
              (item) => item !== value
            ),
          });
        },
      });
    });
  }

  appendStageDateTags(tags, stage, fields.dateRange);
};

export const buildIntegrationFilterTags = (
  filter: IntegrationFilterState,
  consultantLabelById: Record<string, string> = {}
): IntegrationFilterTag[] => {
  const tags: IntegrationFilterTag[] = [];

  appendArrayFieldTags(tags, filter, "partnerType");
  appendArrayFieldTags(tags, filter, "region");
  appendArrayFieldTags(tags, filter, "consultantId", (value) =>
    consultantLabelById[value] ? `Consultant: ${consultantLabelById[value]}` : value
  );
  appendArrayFieldTags(tags, filter, "technology");
  appendArrayFieldTags(tags, filter, "validationStatus", (value) =>
    `Integration: ${value}`
  );
  appendArrayFieldTags(tags, filter, "integrationType");

  filter.integrationStages.forEach((stage) => {
    const fields = filter.stageFilters[stage];
    if (!fields) return;
    appendStageFieldTags(tags, stage, fields);
  });

  return tags;
};
